import * as Service from 'types/service';
import { DEFAULT_WALK_IN_NAME } from 'utils/service/walkInUtils';
import { DisplayBookingFee } from 'components/service/party-panel/DisplayBookingFee';
import { DisplayTime } from '@tock/time';
import {
  PartyForCard,
  getDescriptionString,
  isServiceParty,
} from 'components/service/party/partyRedesignCard/partyType';
import { Typography } from 'bento';
import { WaitlistIcons } from 'components/service/party/partyRedesignCard/WaitlistIcons';
import { dashboard } from 'types';
import {
  deliveryServiceProviderDisplayName,
  isThirdPartyDeliveryVariety,
} from '@tock/shared/utils/offerings';
import { guestListShowNotesSelector } from 'selectors/dashboardPreferenceSelectors';
import { isPartyOnlineWalkIn, isPartyQuotedWalkIn } from 'utils/partyUtils';
import {
  partyCardCompactDescriptionCssDefs,
  partyCardDescriptionCssDefs,
} from 'components/service/party/partyRedesignCard/partyCardCssDefs';
import { useIsCompactView } from 'hooks/useIsCompactView';
import { usePartyHeader } from 'components/service/party/partyRedesignCard/usePartyHeader';
import { useReservationDescription } from 'components/service/party/partyRedesignCard/useReservationDescription';
import { useSelector } from 'react-redux';
import FeatureFlag, { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import PartyRedesignDuration from 'components/service/party/PartyRedesignDuration';
import PartyRedesignIcons from 'components/service/party/partyRedesignCard/PartyRedesignIcons';
import PartyRedesignVisitNotes, {
  BareVisitNotes,
} from 'components/service/party/partyRedesignCard/PartyRedesignVisitNotes';
import PartyVisitCount from 'components/service/party/partyRedesignCard/PartyVisitCount';

interface Props {
  party: PartyForCard;
}

interface ServiceProps {
  party: Service.Party;
}

interface WaitlistProps {
  party: dashboard.CalendarWaitlist;
}

const ReservationDescription: React.FC<Props> = ({ party }) => {
  const isCompactView = useIsCompactView();
  const isVisitCountEnabled = useFeatureFlag('DASHBOARD_COMPACT_VIEW_VISIT_COUNT');
  const { seatingAreaDisplayName, descriptionString } = useReservationDescription(party);

  const visitNotes = <PartyRedesignVisitNotes party={party} />;

  return isCompactView ? (
    <div css={partyCardCompactDescriptionCssDefs.descriptionContainer}>
      <div css={partyCardCompactDescriptionCssDefs.reservationDescription}>
        <Typography
          variant="overline"
          color="subtle"
          css={partyCardDescriptionCssDefs.textOverflow}
        >
          {descriptionString}
          {seatingAreaDisplayName}
        </Typography>
      </div>
      {isVisitCountEnabled && (
        <div css={partyCardCompactDescriptionCssDefs.visitCountContainer}>
          <PartyVisitCount party={party} />
        </div>
      )}
      <div css={partyCardCompactDescriptionCssDefs.iconsContainer}>
        <PartyRedesignIcons party={party} renderDot={!isCompactView} />
        {visitNotes}
      </div>
    </div>
  ) : (
    <>
      <div css={partyCardDescriptionCssDefs.descriptionContent({ isCompactView })}>
        <div>
          <Typography
            variant="overline"
            color="subtle"
            css={partyCardDescriptionCssDefs.textOverflow}
          >
            {descriptionString}
            {seatingAreaDisplayName}
          </Typography>
        </div>
        <PartyRedesignIcons party={party} renderDot={!isCompactView} />
      </div>
      {visitNotes}
    </>
  );
};

const WalkinDescription: React.FC<ServiceProps> = ({ party, party: { state } }) => {
  const isCompactView = useIsCompactView();
  const descriptionString = getDescriptionString(party);
  const partyIsOnlineWalkin = isPartyOnlineWalkIn(party);
  const isVisitCountEnabled = useFeatureFlag('DASHBOARD_COMPACT_VIEW_VISIT_COUNT');
  const { name } = usePartyHeader(party);
  const descriptionContent = (
    <Typography variant="overline" color="subtle" css={partyCardDescriptionCssDefs.textOverflow}>
      {descriptionString}
    </Typography>
  );

  switch (state) {
    case 'EXPECTED':
    case 'PARTIALLY_ARRIVED':
    case 'ARRIVED':
      if (isCompactView) {
        return (
          <div css={partyCardDescriptionCssDefs.descriptionContent({ isCompactView })}>
            <PartyRedesignIcons party={party} />
            <div css={partyCardDescriptionCssDefs.notesAndDurationContainer}>
              <Typography
                variant="body"
                color="subtle"
                css={partyCardDescriptionCssDefs.textOverflow}
              >
                {party.shortNotes}
              </Typography>
              <PartyRedesignDuration party={party} />
            </div>
          </div>
        );
      } else {
        return (
          <>
            {partyIsOnlineWalkin && (
              <Typography variant="overline" color="subtle">
                Online
              </Typography>
            )}
            <div css={partyCardDescriptionCssDefs.descriptionContent({ isCompactView })}>
              <PartyRedesignDuration party={party} />
              {name !== DEFAULT_WALK_IN_NAME && !isPartyQuotedWalkIn(party) && descriptionContent}
              <PartyRedesignIcons party={party} renderDot />
            </div>
            <BareVisitNotes>{party.shortNotes}</BareVisitNotes>
          </>
        );
      }
    case 'PARTIALLY_SEATED':
    case 'SEATED':
    case 'LEFT':
    case 'CANCELED':
    case 'NO_SHOW':
    default:
      if (isCompactView) {
        return (
          <div css={partyCardCompactDescriptionCssDefs.descriptionContainer}>
            <div css={partyCardCompactDescriptionCssDefs.reservationDescription}>
              {descriptionContent}
            </div>
            {isVisitCountEnabled && (
              <div css={partyCardCompactDescriptionCssDefs.visitCountContainer}>
                <PartyVisitCount party={party} />
              </div>
            )}
            <div css={partyCardCompactDescriptionCssDefs.iconsContainer}>
              <PartyRedesignIcons party={party} renderDot={!isCompactView} />
              <Typography
                variant="body"
                color="subtle"
                css={partyCardDescriptionCssDefs.textOverflow}
              >
                {party.shortNotes}
              </Typography>
            </div>
          </div>
        );
      } else {
        return (
          <>
            <div css={partyCardDescriptionCssDefs.descriptionContent({ isCompactView })}>
              <div>{name !== DEFAULT_WALK_IN_NAME && descriptionContent}</div>
              <PartyRedesignIcons party={party} renderDot={!isCompactView} />
            </div>
            <BareVisitNotes>{party.shortNotes}</BareVisitNotes>
          </>
        );
      }
  }
};

const DeliveryPickupDescription: React.FC<ServiceProps> = ({ party }) => {
  const isCompactView = useIsCompactView();
  const ticketType = party.purchase?.ticketType;

  const isDelivery = party.purchase?.ticketType?.variety === 'ORDER_DELIVERY';
  const isPickup = party.purchase?.ticketType?.variety === 'ORDER_PICKUP';

  const getDeliveryOrPickupText = () => {
    if (isDelivery) {
      if (isThirdPartyDeliveryVariety(ticketType)) {
        return ` · ${deliveryServiceProviderDisplayName(
          ticketType.deliveryServiceProvider
        )} delivery`;
      }

      if (party.purchase?.ticketType?.deliveryServiceProvider === 'INTERNAL') {
        return ' · delivery';
      }
    }

    if (isPickup) {
      return ' · pickup';
    }

    return '';
  };

  return (
    <>
      <div css={partyCardDescriptionCssDefs.descriptionContent({ isCompactView })}>
        <Typography variant="overline" color="subtle">
          {getDescriptionString(party)}
          <FeatureFlag ifEnabled="SHOW_DELIVERY_ORDER_AT_PICKUP_TIME">
            {(showDeliveryOrderAtPickupTime) => (
              <>
                {isDelivery &&
                  (showDeliveryOrderAtPickupTime || isThirdPartyDeliveryVariety(ticketType)) && (
                    <>
                      {' · '} <DisplayTime timeValue={party.ticketDateTime} />
                    </>
                  )}
              </>
            )}
          </FeatureFlag>
          {getDeliveryOrPickupText()}
        </Typography>
        <PartyRedesignIcons party={party} renderDot={!isCompactView} />
        <PartyRedesignVisitNotes party={party} />
      </div>
    </>
  );
};

const CancelledAndNoShowDescription: React.FC<ServiceProps> = ({ party }) => {
  const isEligibleForHoldCharge = party.purchase?.isEligibleForBookingPolicyFee ?? false;
  const isEligibleForRefund = party.purchase?.paymentCardHold?.status === 'HOLD_CHARGED';

  return (
    <DisplayBookingFee
      paymentCardHold={party.purchase?.paymentCardHold}
      isEligibleForCharge={isEligibleForHoldCharge}
      isEligibleForRefund={isEligibleForRefund}
    />
  );
};

const WaitlistDescription: React.FC<WaitlistProps> = ({ party: waitlistRequest }) => {
  const isCompactView = useIsCompactView();
  const showGuestListNotes = useSelector(guestListShowNotesSelector);

  const shortCode = waitlistRequest?.desiredExperience?.shortCode
    ? waitlistRequest.desiredExperience.shortCode
    : 'ANY';

  const waitlistNote =
    showGuestListNotes && waitlistRequest?.note ? (
      <PartyRedesignVisitNotes party={waitlistRequest} />
    ) : null;

  return (
    <>
      <div css={partyCardDescriptionCssDefs.descriptionContent({ isCompactView })}>
        <Typography variant="overline" color="subtle">
          {shortCode}
        </Typography>
        <WaitlistIcons waitlistRequest={waitlistRequest} />
        {/* if we are in compact view the waitlist note goes here */}
        {isCompactView && waitlistNote}
      </div>
      {/* if we are not in compact view the waitlist note goes here */}
      {!isCompactView && waitlistNote}
    </>
  );
};

const PartyRedesignDescription: React.FC<Props> = ({ party }) => {
  if (!isServiceParty(party)) {
    return <WaitlistDescription party={party} />;
  }

  const isPartyNoShowOrCancellation =
    isServiceParty(party) &&
    (party.purchase?.primaryState === 'NO_SHOW' || party.purchase?.primaryState === 'CANCELED');

  const isDeliveryOrPickup =
    isServiceParty(party) &&
    (party.purchase?.ticketType?.variety === 'ORDER_DELIVERY' ||
      party.purchase?.ticketType?.variety === 'ORDER_PICKUP');
  const isWalkin = party.type === 'WalkIn';

  let DescriptionComponent;

  if (isPartyNoShowOrCancellation) {
    DescriptionComponent = CancelledAndNoShowDescription;
  } else if (isWalkin) {
    DescriptionComponent = WalkinDescription;
  } else if (isDeliveryOrPickup) {
    DescriptionComponent = DeliveryPickupDescription;
  } else {
    DescriptionComponent = ReservationDescription;
  }
  return (
    <div data-testid="party-description" css={partyCardDescriptionCssDefs.descriptionContainer}>
      <DescriptionComponent party={party} />
    </div>
  );
};

export default PartyRedesignDescription;
